<template>
  <form class="products" @submit.prevent="submit">
    <app-fieldset :title="`Product ${buttonTitle}`">
      <app-form-field
        id="name"
        class="form-item-name"
        :title="i18n.t('/EditProduct/NameLabel')"
        :error-message="errorMessage"
      >
        <autocomplete
          id="name"
          v-autofocus
          data-qa="dealer-option-item-input-field"
          :list="autocompleteList"
          :model-value="name"
          :min-chars="1"
          @select="updateName"
          @blur="updateName"
        />
      </app-form-field>

      <app-form-field id="vatRates" :title="i18n.t('/EditProduct/VatLabel')">
        <form-radio-button
          v-for="VatRate in vatRates"
          :id="`vat-${VatRate.vatValue}`"
          :key="VatRate.vatValue"
          :title="`${VatRate.vatValue}%`"
        >
          <input
            :id="`vat-${VatRate.vatValue}`"
            v-model="VAT"
            :value="VatRate.vatValue"
            :data-qa="`vat-${VatRate.vatValue}`"
            name="vat"
            type="radio"
            aria-describedby="error-message-vat"
            @change="updateVAT"
          />
        </form-radio-button>
      </app-form-field>

      <template v-if="showPurchasePrice">
        <price-input
          id="price-excl"
          v-model="purchaseExcl"
          name="price-excl"
          :title="i18n.t('/EditProduct/PurchasePriceLabel')"
          @change="updatePurchaseExcl"
        />

        <app-form-field :title="i18n.t('/EditProduct/SalesPriceLabel')">
          <div class="value">{{ priceExcl }}</div>
        </app-form-field>
      </template>
      <template v-else>
        <price-input
          id="price-excl"
          v-model="priceExcl"
          name="price-excl"
          :title="i18n.t('/EditProduct/BtwExcludedLabel')"
          @change="updatePriceExcl"
        />
        <price-input
          id="price-incl"
          v-model="priceIncl"
          name="price-incl"
          :title="i18n.t('/EditProduct/BtwIncludedLabel')"
          @change="updatePriceIncl"
        />
      </template>
    </app-fieldset>

    <app-button
      v-if="showResetToOriginal"
      button-class="secondary"
      data-qa="reset-to-original-price-button"
      @click="resetToOriginal"
    >
      {{ resetToOriginalLabel }}
    </app-button>
    <app-button data-qa="edit-product-button" button-type="submit">{{ buttonTitle }}</app-button>
  </form>
</template>

<script>
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import HandleAccessoryEvents from '@/mixins/handleAccessoryEvents.js'
import autoFocus from '@/mixins/autoFocus.js'

import FormRadioButton from '@/components/generic/presentational/atoms/formRadioButton/FormRadioButton.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import PriceInput from '@/components/generic/presentational/atoms/priceInput/PriceInput.vue'
import AppFormField from '@/components/generic/presentational/atoms/formField/FormField.vue'

import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import Autocomplete from '@/components/generic/presentational/molecules/autocomplete/Autocomplete.vue'
import StoreConnector from '@/helpers/StoreConnector'
import constants from '@/utils/constants'
import currencyHelper from '@/helpers/currencyHelper'
import { mapGetters } from 'vuex'

/**
 * `submit()` writes the edited values into `editProduct`, so `editProduct` has to
 * be an object this component owns. Its two sources are aliases of something else:
 * the `product` prop belongs to the parent, and `foundAccessory` is a live
 * reference into the `accessories` store, which the reset-to-original feature
 * reads back as the untouched catalog price. A shallow copy is not enough - the
 * price is written field by field through the nested `Price` object.
 */
const cloneProduct = (source) => ({
  ...source,
  Price: { ...(source?.Price || {}) },
  PackageSubItems: Array.isArray(source?.PackageSubItems)
    ? [...source.PackageSubItems]
    : source?.PackageSubItems,
})

/**
 * Amounts can reach this component either as a number (from PriceInput) or as an
 * NL formatted string coming from the API ('1234,56'), so they are normalised
 * before they are compared or assigned. Anything unparseable becomes 0 instead
 * of NaN, which would otherwise never compare equal to itself.
 */
const toAmount = (value) => {
  const number = currencyHelper.fromString(value)
  return Number.isFinite(number) ? number : 0
}

export default {
  components: {
    AppButton,
    PriceInput,
    AppFieldset,
    AppFormField,
    FormRadioButton,
    Autocomplete,
  },
  mixins: [HandleAccessoryEvents, autoFocus],
  props: {
    product: {
      type: Object,
      default: () => {},
    },
    showPurchasePrice: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    isTyresProducts: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional catalog original (Name + Price) of the product being edited.
     * When set and the current values differ, a reset button restores them.
     * Read only, never mutated by this component.
     */
    originalProduct: {
      type: Object,
      default: null,
    },
  },
  emits: ['close-edit-modal'],
  setup() {
    return { v$: useVuelidate() }
  },
  validations: {
    name: {
      required,
    },
  },
  data() {
    return {
      accessories: [],
      name: '',
      priceExcl: 0,
      priceIncl: 0,
      purchaseExcl: null,
      VAT: 21,
      editProduct: {
        Price: {},
      },
      foundAccessory: null,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    ...mapGetters({
      quoteAccessories: 'quote/accessories',
    }),
    buttonTitle() {
      if (this.product) {
        return this.i18n.t('/EditProduct/ChangeAccessoryButton')
      }
      return this.i18n.t('/EditProduct/AddAccessoryButton')
    },
    vatRates() {
      const vatRates = Object.assign([], constants.VAT_RATES)
      vatRates.sort((a, b) => {
        return a.vatValue - b.vatValue
      })
      return vatRates
    },
    autocompleteList() {
      if (this.accessories) return this.accessories.map((item) => item.Name)
      return []
    },
    errorMessage() {
      return this.v$.name.$anyDirty && !this.v$.name.required.$response
        ? this.i18n.t('/EditProduct/NameValidationError')
        : ''
    },
    resetToOriginalLabel() {
      return this.i18n.t('/EditProduct/ResetToOriginalLabel')
    },
    showResetToOriginal() {
      if (!this.originalProduct) return false
      const originalPrice = this.originalProduct.Price || {}
      return (
        this.name !== this.originalProduct.Name ||
        toAmount(this.priceExcl) !== toAmount(originalPrice.Excl) ||
        toAmount(this.VAT) !== toAmount(originalPrice.BtwRate)
      )
    },
  },
  created() {
    if (this.product) {
      this.editProduct = cloneProduct(this.product)

      this.name = this.product.Name
      this.VAT = Number(this.product.Price.BtwRate)
      this.priceExcl = Number(this.product.Price.Excl)
      this.priceIncl = Number(this.product.Price.Incl)
      this.purchaseExcl = Number(this.product.Price.PurchaseExcl)
      this.Btw = Number(this.product.Price.Btw)
    }

    StoreConnector.getWithParam(
      'accessories/accessoriesWithCategories',
      this.categories,
      (data) => {
        this.accessories = data.filter(
          (item) =>
            item.ID === this.product?.ID ||
            !this.quoteAccessories.map((accessory) => accessory.ID).includes(item.ID)
        )
      }
    )
  },
  methods: {
    // Restores both the catalog name and the catalog prices
    resetToOriginal() {
      if (!this.originalProduct) return
      const originalPrice = this.originalProduct.Price || {}

      this.name = this.originalProduct.Name
      this.VAT = toAmount(originalPrice.BtwRate)
      this.priceExcl = toAmount(originalPrice.Excl)
      this.priceIncl = toAmount(originalPrice.Incl)
    },
    updateName(value, updateNameOnly = false) {
      if (updateNameOnly && this.name === value) return

      this.name = value
      if (updateNameOnly || !this.accessories) return
      this.foundAccessory = this.accessories.find((accessory) => accessory.Name === value)
      if (this.foundAccessory) {
        this.VAT = Number(this.foundAccessory.Price.BtwRate)
        this.priceExcl = Number(this.foundAccessory.Price.Excl)
        this.priceIncl = Number(this.foundAccessory.Price.Incl)
        this.purchaseExcl = Number(this.foundAccessory.Price.PurchaseExcl)
        this.Btw = Number(this.foundAccessory.Price.Btw)
      }
    },
    updateVAT() {
      this.priceIncl = this.calculatePriceIncl(this.priceExcl, this.VAT)
    },
    updatePriceExcl(inp) {
      this.priceExcl = inp
      this.priceIncl = this.calculatePriceIncl(inp, this.VAT)
    },
    updatePriceIncl(inp) {
      this.priceIncl = inp
      this.priceExcl = this.calculatePriceExcl(inp, this.VAT)
    },
    updatePurchaseExcl(inp) {
      this.purchaseExcl = inp
    },
    calculatePriceIncl(priceExcl, VAT) {
      return priceExcl + priceExcl * (Number(VAT) / 100)
    },
    calculatePriceExcl(priceIncl, VAT) {
      return 100 * (priceIncl / (Number(VAT) + 100))
    },
    validate() {
      this.v$.$touch()
      return !this.v$.$invalid
    },
    submit() {
      if (this.validate()) {
        if (this.foundAccessory) {
          this.editProduct = cloneProduct(this.foundAccessory)
        } else {
          // Set the category so accessory will be visible while loading
          this.editProduct.Category = this.categories[0]
        }

        this.editProduct.Name = this.name
        this.editProduct.Price.BtwRate = this.VAT
        this.editProduct.Price.Excl = this.priceExcl
        this.editProduct.Price.Incl = this.priceIncl
        this.editProduct.Price.Btw = this.priceIncl - this.priceExcl

        if (this.editProduct.PackageSubItems === undefined) this.editProduct.PackageSubItems = []

        //In case of new accessory the purchasePrice needs to be the same as the excl price
        if (this.purchaseExcl == null) {
          this.editProduct.Price.PurchaseExcl = this.priceExcl
        } else {
          this.editProduct.Price.PurchaseExcl = this.purchaseExcl
        }

        if (this.isTyresProducts) {
          this.resetAllWeatherTyresPL(false)
          this.editProduct.AllWeatherTyresPL = true
        }

        if (this.foundAccessory || !this.editProduct.OptionType_ID) {
          // Calls method from HandleAccessoryEvents mixin
          this.addToSelected(this.editProduct)
        } else {
          // Calls method from HandleAccessoryEvents mixin
          this.modifyAccessory(this.editProduct)
        }

        this.$emit('close-edit-modal', this.editProduct)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.fields) {
  grid-template-columns: repeat(3, 1fr);
}

.form-item-name {
  grid-column: span 3;
}
</style>
